//---------------------------------------------------------
// Tile scrolling (owner ask 2026-10-15, UX lock).
// One shared scroll body for every dashboard tile whose content can overflow.
// - Only this body scrolls; the CardBase title and any caption/column header
//   rendered outside it stay fixed. The tile height never changes.
// - Rows are marked with TILE_ROW (data-tile-row). The body's height is
//   trimmed to the bottom of the last row that fits whole, and scrolling
//   snaps to row starts, so no row is ever cut at an edge.
// - Thin (6px) scrollbar in the theme divider color, only when overflowing,
//   with a stable gutter so nothing shifts when it appears.
// - 16px bottom fade in the tile background while more is below; none at top.
// - Overflowing body: tabIndex=0, focus ring, role=region named by the tile
//   title; arrow keys / PgUp / PgDn scroll it natively. Otherwise not focusable.
//---------------------------------------------------------
import {Box, SxProps, Theme} from '@mui/material';
import {ReactNode, useCallback, useLayoutEffect, useRef, useState} from 'react';

/** Spread onto each list row: `<Box {...TILE_ROW}>`. */
export const TILE_ROW = {'data-tile-row': ''} as const;
const ROW_SELECTOR = '[data-tile-row]';
export const FADE_PX = 16;

export interface IScrollMetrics {
	scrollTop: number;
	scrollHeight: number;
	clientHeight: number;
}

/** Pure: does the content overflow, and is there more below the visible edge? */
export function scrollState(m: IScrollMetrics): {overflows: boolean; moreBelow: boolean} {
	const overflows = m.scrollHeight - m.clientHeight > 1;
	const moreBelow = overflows && m.scrollTop + m.clientHeight < m.scrollHeight - 1;
	return {overflows, moreBelow};
}

/**
 * Pure: the tallest height <= available that ends exactly on a row bottom.
 * rowBottoms are offsets from the top of the scroll content. Returns
 * undefined when no rows are marked or everything fits (use full height).
 */
export function wholeRowHeight(available: number, rowBottoms: number[]): number | undefined {
	if (rowBottoms.length === 0 || available <= 0) return undefined;
	if (rowBottoms[rowBottoms.length - 1] <= available) return undefined;
	let best = 0;
	for (const b of rowBottoms) if (b <= available && b > best) best = b;
	return best > 0 ? best : undefined;
}

export interface ITileScrollBodyProps {
	/** Accessible name; pass the tile title. */
	label: string;
	children: ReactNode;
	sx?: SxProps<Theme>;
	'data-testid'?: string;
}

export default function TileScrollBody(props: ITileScrollBodyProps) {
	const {label, children, sx} = props;
	const outerRef = useRef<HTMLDivElement>(null);
	const scrollRef = useRef<HTMLDivElement>(null);
	const [state, setState] = useState({overflows: false, moreBelow: false});
	const [height, setHeight] = useState<number | undefined>(undefined);

	const measure = useCallback(() => {
		const outer = outerRef.current;
		const el = scrollRef.current;
		if (!outer || !el) return;
		const rows = Array.from(el.querySelectorAll<HTMLElement>(ROW_SELECTOR));
		const top = el.getBoundingClientRect().top - el.scrollTop;
		const bottoms = rows.map(r => {
			const rect = r.getBoundingClientRect();
			return Math.ceil(rect.bottom - top);
		});
		const h = wholeRowHeight(outer.clientHeight, bottoms);
		setHeight(prev => (prev === h ? prev : h));
		const next = scrollState(el);
		setState(prev => (prev.overflows === next.overflows && prev.moreBelow === next.moreBelow ? prev : next));
	}, []);

	useLayoutEffect(() => {
		measure();
		if (typeof ResizeObserver === 'undefined') return;
		const ro = new ResizeObserver(() => measure());
		if (outerRef.current) ro.observe(outerRef.current);
		if (scrollRef.current) {
			ro.observe(scrollRef.current);
			Array.from(scrollRef.current.children).forEach(c => ro.observe(c));
		}
		return () => ro.disconnect();
	});

	const onScroll = () => {
		const el = scrollRef.current;
		if (!el) return;
		const next = scrollState(el);
		setState(prev => (prev.overflows === next.overflows && prev.moreBelow === next.moreBelow ? prev : next));
	};

	return (
		<Box ref={outerRef} position="relative" flex="1 1 auto" minHeight={0} width="100%" sx={{overflow: 'hidden', ...(sx as object)}}>
			<Box
				ref={scrollRef}
				data-testid={props['data-testid'] ?? 'tile-scroll-body'}
				data-overflows={state.overflows ? 'true' : 'false'}
				onScroll={onScroll}
				tabIndex={state.overflows ? 0 : undefined}
				role={state.overflows ? 'region' : undefined}
				aria-label={state.overflows ? label : undefined}
				sx={theme => ({
					height: height !== undefined ? `${height}px` : '100%',
					maxHeight: '100%',
					overflowY: 'auto',
					overflowX: 'hidden',
					scrollSnapType: 'y mandatory',
					scrollbarGutter: 'stable',
					scrollbarWidth: 'thin',
					scrollbarColor: `${theme.palette.divider} transparent`,
					'&::-webkit-scrollbar': {width: 6},
					'&::-webkit-scrollbar-thumb': {backgroundColor: theme.palette.divider, borderRadius: 3},
					'&::-webkit-scrollbar-track': {background: 'transparent'},
					[`& ${ROW_SELECTOR}`]: {scrollSnapAlign: 'start'},
					borderRadius: '4px',
					'&:focus': {outline: 'none'},
					'&:focus-visible': {outline: '2px solid #164E77', outlineOffset: '-2px'},
				})}
			>
				{children}
			</Box>
			{state.moreBelow && (
				<Box
					aria-hidden
					data-testid="tile-scroll-fade"
					sx={theme => ({
						position: 'absolute',
						left: 0,
						right: 0,
						top: height !== undefined ? `${height - FADE_PX}px` : undefined,
						bottom: height !== undefined ? undefined : 0,
						height: FADE_PX,
						pointerEvents: 'none',
						background: `linear-gradient(to bottom, transparent, ${theme.palette.background.paper})`,
					})}
				/>
			)}
		</Box>
	);
}
